<template>
  <a-modal :open="open" @cancel="closeForm" class="modal-no-padding">
    <div class="select-users-wrapper">
    <div class="modal-no-padding__header">
      <h2>Добавление сотрудников в группу</h2>
    </div>
    <a-divider style="margin: 16px 0" />

      <div class="modal-no-padding__body">
        <span>Действующие сотрудники</span>

        <a-select
            v-model:value="groupForm.users"
            show-search
            allow-clear
            mode="multiple"
            :placeholder="'Не выбрано'"
            :not-found-content="usersLoading ? 'Поиск…' : 'Нет данных'"
            :loading="usersLoading"
            :filter-option="false"
            :options="userOptions"
            @search="debouncedSearch"
            @clear="clearUsers"
            class="select-users fixed-select"
            :show-arrow="true"
        />

        <a-button type="text" class="add-user-button" @click="showNewStaffForm = true">
          <template #icon>
            <PlusOutlined />
          </template>
          Добавить сотрудника
        </a-button>
      </div>
    </div>

    <template #footer>
      <a-divider style="margin: 16px 0" />
      <div class="modal-no-padding__footer">
        <div class="action-buttons">
          <a-row justify="end" :gutter="8">
            <a-col>
              <a-button class="secondary-button" @click="closeForm">Отменить</a-button>
            </a-col>
            <a-col>
              <a-button class="primary-button" :loading="modalActionLoading" @click="handleGroupEdit">Добавить</a-button>
            </a-col>
          </a-row>
        </div>
      </div>
    </template>
  </a-modal>

  <NewStaffForm
      v-if="showNewStaffForm"
      :open="showNewStaffForm"
      :selectedGroups="[currentGroup?.id]"
      @close="showNewStaffForm = false"
      @userCreated="handleUserCreated"
  />
</template>

<script>
import {message} from "ant-design-vue";
import {useStore} from "vuex";
import {onBeforeMount, onBeforeUnmount, reactive, ref} from "@vue/runtime-core";
import debounce from "@/mixins/debounce";
import {computed} from "@vue/reactivity";
import NewStaffForm from "@/views/Staff/NewStaffForm.vue";
import {PlusOutlined} from "@ant-design/icons-vue";

export default {
  components: {NewStaffForm, PlusOutlined},
  props: {
    open: Boolean,
    currentGroup: Object,
  },
  emits: ['submitted', 'close', 'usersCreated'],
  setup(props, context) {
    const store = useStore();
    const modalActionLoading = ref(false);
    const groupToCopyIn = ref();
    const foundUsers = computed(() => store.state.users.foundUsers);
    const addedUsersToGroup = computed(() => store.state.users.addedUsersToGroup);
    const showNewStaffForm = ref(false);

    const groupForm = reactive({
      name: props.currentGroup?.name ?? '',
      users: props.currentGroup?.users?.map((user) => user.id) ?? [],
    });

    const statusError = reactive({
      groupName: false,
    });

    const usersLoading = ref(false);
    const userOptions = ref([]);

    let currentSearchRequest = ref(0);

    const getUserOption = (user) => {
      return {
        value: user.id,
        label: user.email ? `${user.name} (${user.email})` : user.name,
      };
    }

    const addUserOptions = (users) => {
      (users ?? [])
          .filter((user) => user?.id)
          .forEach((user) => {
            if (!userOptions.value.some((opt) => opt.value === user.id)) {
              userOptions.value.push(getUserOption(user));
            }
          });
    }

    const addUsersToSelection = (users) => {
      (users ?? [])
          .filter((user) => user?.id)
          .forEach((user) => {
            if (!groupForm.users.includes(user.id)) {
              groupForm.users.push(user.id);
            }
          });
    }

    const searchForUsers = async (query) => {
      fillUsers();

      if (!query || query.length < 3) return;

      let requestNumber = ++currentSearchRequest.value;
      usersLoading.value = true;

      userOptions.value = [];
      addUserOptions(props.currentGroup?.users);
      addUserOptions(addedUsersToGroup.value);

      store.dispatch('users/searchByName', query).then(() => {
        if (requestNumber !== currentSearchRequest.value) return;

        const uniqueNew = (foundUsers.value ?? [])
            .filter(u => !userOptions.value.some((opt) => {
              return u.id === opt.value;
            }))
            .map(u => ({
              value: u.id,
              label: `${u.name} (${u.email})`,
            }));

        userOptions.value.push(...uniqueNew);
        usersLoading.value = false
      });
    }

    const debouncedSearch = debounce(searchForUsers, 350);

    const clearUsers = () => {
      fillUsers();
    }

    const fillUsers = () => {
      userOptions.value = [];
      addUserOptions(props.currentGroup?.users);
      addUserOptions(addedUsersToGroup.value);
    }

    const clearUsersToGroupDraft = () => {
      store.dispatch('users/clearUsersToGroupDraft');
    }

    const handleUserCreated = (createdUsers) => {
      const users = Array.isArray(createdUsers) ? createdUsers : [createdUsers];
      showNewStaffForm.value = false;

      store.dispatch('users/addUsersToGroupDraft', users);
      addUserOptions(users);
      addUsersToSelection(users);
      context.emit('usersCreated');
    }

    const handleGroupEdit = () => {
      modalActionLoading.value = true;

      if (!groupForm.name) {
        statusError.groupName = true;
        modalActionLoading.value = false;
        return;
      } else {
        statusError.groupName = false;
      }

      store.dispatch('group/updateGroup', {group: {...groupForm}, id: props.currentGroup.id}).then(() => {
        store.dispatch("group/getGroup", props.currentGroup.id);
        message.success('Данные обновлены');
        store.dispatch('users/clearFoundUsers');
        clearUsersToGroupDraft();
        context.emit('submitted')
      }).finally(() => modalActionLoading.value = false);
    }

    const closeForm = () => {
      clearUsersToGroupDraft();
      context.emit('close');
    }

    onBeforeMount(() => {
      fillUsers();
    });

    onBeforeUnmount(() => {
      clearUsersToGroupDraft();
    });

    return {
      groupToCopyIn,
      modalActionLoading,
      closeForm,
      groupForm,
      statusError,
      handleGroupEdit,
      searchForUsers,
      usersLoading,
      debouncedSearch,
      userOptions,
      clearUsers,
      showNewStaffForm,
      handleUserCreated,
    }
  }
}
</script>

<style scoped lang="scss">
.select-users-wrapper {
  display: flex;
  flex-direction: column;
  row-gap: 5px;

  h2 {
    margin: 0;
    font-weight: 600;
    font-size: 16px;
    line-height: 120%;
  }
}

.select-users {
  width: 100%;
}

.add-user-button {
  align-self: flex-start;
  padding-left: 0;
  margin-top: 10px;
  color: var(--main-green);
}
</style>
